import { useState } from "react";
import { X } from "lucide-react";
import { ActiveFilterToken } from "../../../core/store/useQuestionFiltersStore";
import { FOCUS_RING, cx } from "./tokens";

export interface ActiveFiltersProps {
  items: ActiveFilterToken[];
  onRemove: (item: ActiveFilterToken) => void;
  onClearAll: () => void;
  /**
   * `panel` — the sidebar card.
   * `inline` — the horizontally scrollable row under the header on small
   *   screens, inside the panel (§23.9).
   * `bar` — the closed strip in the app shell. Never scrolls sideways: past a
   *   handful of tokens it collapses to a link, because a scrollbar hides how
   *   much is selected behind a gesture nobody performs.
   */
  variant?: "panel" | "inline" | "bar";
  /** `bar` only — how many tokens show before collapsing to the link. */
  maxVisible?: number;
  className?: string;
}

/**
 * What is currently affecting the query.
 *
 * Every token is prefixed with its group so it stays readable out of context,
 * removes exactly one value, and truncates at the end with the full text kept
 * in `title`. `Limpar tudo` is neutral, not red — clearing a filter destroys
 * nothing the user cannot immediately redo (§54).
 */
export default function ActiveFilters({
  items,
  onRemove,
  onClearAll,
  variant = "panel",
  maxVisible = 3,
  className,
}: ActiveFiltersProps) {
  const isPanel = variant === "panel";
  const [expanded, setExpanded] = useState(false);

  const linkClass = cx(
    "shrink-0 rounded-chip px-2 py-1 text-xs font-medium",
    "text-brand transition-colors hover:bg-surface-hover",
    FOCUS_RING,
  );

  const clearClass = cx(
    "shrink-0 rounded-chip px-2 py-1 text-xs font-medium",
    "text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink",
    FOCUS_RING,
  );

  const tokens = items.map((item) => {
    const text = `${item.dimensionLabel}: ${item.value}`;
    return (
      <span
        key={`${item.dimension}:${item.value}`}
        className={cx(
          "inline-flex h-[26px] max-w-full shrink-0 items-center gap-1 rounded-full",
          "border border-edge bg-surface-muted pl-2.5 pr-1 text-xs text-ink-secondary",
        )}
      >
        <span className="truncate" title={text}>
          {text}
        </span>
        <button
          type="button"
          onClick={() => onRemove(item)}
          aria-label={`Remover filtro ${text}`}
          title={`Remover filtro ${text}`}
          className={cx(
            "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full",
            "text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink",
            FOCUS_RING,
          )}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
    );
  });

  if (variant === "bar") {
    if (items.length === 0) return null;

    const collapsed = items.length > maxVisible && !expanded;

    return (
      <div
        className={cx("flex flex-wrap items-center gap-x-2 gap-y-1.5", className)}
        role="group"
        aria-label="Filtros selecionados"
      >
        {collapsed ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
            className={linkClass}
          >
            {`Ver os ${items.length} filtros selecionados`}
          </button>
        ) : (
          <>
            {tokens}
            {items.length > maxVisible ? (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-expanded
                className={linkClass}
              >
                Ocultar
              </button>
            ) : null}
            <button type="button" onClick={onClearAll} className={clearClass}>
              Limpar tudo
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isPanel) {
    if (items.length === 0) return null;
    return (
      <div
        className={cx("flex items-center gap-2 overflow-x-auto pb-1", className)}
        role="group"
        aria-label="Filtros selecionados"
      >
        {tokens}
        <button type="button" onClick={onClearAll} className={clearClass}>
          Limpar tudo
        </button>
      </div>
    );
  }

  return (
    <div
      className={cx("rounded-card border border-edge bg-surface p-4", className)}
      role="group"
      aria-label="Filtros selecionados"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink">Filtros selecionados</h3>
        <span
          className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-select-ink"
          aria-label={`${items.length} filtros selecionados`}
        >
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="mt-3 text-xs text-ink-muted">
          Nenhum filtro selecionado. Escolha um ano, uma banca ou uma matéria para começar.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5">{tokens}</div>
          <button type="button" onClick={onClearAll} className={cx("mt-3", clearClass)}>
            Limpar tudo
          </button>
        </>
      )}
    </div>
  );
}
